import React, { useState, useEffect } from 'react';
import { Menu, X, Thermometer, Fan, Flame, Snowflake, Phone, MapPin } from 'lucide-react';

// Shared orange palette - matching Summer Beat theme
const colors = {
  primary: '#FF8A00',
  primaryDark: '#FF6A00',
  softBg: 'rgba(255, 255, 255, 0.9)',
  textPrimary: '#1A1A1A',
  textMuted: '#666666',
  borderSoft: 'rgba(255, 138, 0, 0.16)',
  shadowSoftScrolled: '0 10px 30px rgba(0, 0, 0, 0.12)',
  shadowSoftTop: '0 4px 18px rgba(0, 0, 0, 0.06)',
  // Summer Beat specific colors
  summer: '#1E88E5',
  winter: '#FF9800',
  cooler: '#00BCD4',
};

// Custom hook for window size
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

function Navbar({
  currentPage,
  setCurrentPage,
  mobileMenuOpen,
  setMobileMenuOpen,
  user,
}) {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [scrollPosition, setScrollPosition] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'products', label: 'Products', submenu: [
      { id: 'summer', label: '☀️ SB Summer', color: colors.summer },
      { id: 'winter', label: '❄️ SB Winter', color: colors.winter },
      { id: 'cooler', label: '💨 SB Cooler', color: colors.cooler }
    ]},
    { id: 'about', label: 'About Us' },
    { id: 'contact', label: 'Contact Us' },
  ];

  const isScrolled = scrollPosition > 10;
  const [showProductMenu, setShowProductMenu] = useState(false);

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        background: isScrolled
          ? 'rgba(255, 255, 255, 0.98)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: isScrolled
          ? colors.shadowSoftScrolled
          : colors.shadowSoftTop,
        borderBottom: `1px solid ${colors.borderSoft}`,
        zIndex: 50,
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 1.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '70px',
          }}
        >
          {/* Logo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition:
                'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
              gap: '0.8rem',
              position: 'relative',
            }}
            onClick={() => setCurrentPage('home')}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #FF8A00, #FF6A00)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: 'bold'
              }}>
                SB
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.15rem',
              }}
            >
              <span
                style={{
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  background:
                    'linear-gradient(120deg, #FF8A00 0%, #FF6A00 50%, #FFB75E 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Summer Beat
              </span>
              <span
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  opacity: 0.8,
                }}
              >
                Home Appliances
              </span>
            </div>
          </div>

          {/* Desktop nav */}
          {!isMobile && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.2rem',
              }}
            >
              {navItems.map((item) => {
                const active = currentPage === item.id;
                const hovered = hoveredItem === item.id;

                if (item.id === 'products') {
                  return (
                    <div key={item.id} style={{ position: 'relative' }}>
                      <button
                        onClick={() => setShowProductMenu(!showProductMenu)}
                        onMouseEnter={() => setShowProductMenu(true)}
                        onMouseLeave={() => setTimeout(() => setShowProductMenu(false), 300)}
                        style={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          padding: '0.55rem 1.15rem',
                          borderRadius: '999px',
                          transition:
                            'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          color: active
                            ? colors.textPrimary
                            : hovered
                            ? colors.textPrimary
                            : colors.textMuted,
                          background: active || showProductMenu
                            ? 'rgba(255, 138, 0, 0.12)'
                            : hovered
                            ? 'rgba(255, 152, 0, 0.08)'
                            : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          position: 'relative',
                          letterSpacing: '0.3px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                        }}
                      >
                        {item.label}
                        {showProductMenu && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '-2px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '60%',
                              height: '3px',
                              background:
                                'linear-gradient(90deg, #FF8A00 0%, #FF6A00 100%)',
                              borderRadius: '999px',
                              boxShadow:
                                '0 2px 10px rgba(255, 138, 0, 0.5)',
                            }}
                          />
                        )}
                      </button>
                      
                      {/* Product Submenu */}
                      {showProductMenu && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'white',
                            borderRadius: '16px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                            padding: '1.5rem',
                            minWidth: '250px',
                            zIndex: 100,
                            border: `1px solid ${colors.borderSoft}`,
                            animation: 'slideDown 0.3s ease-out'
                          }}
                          onMouseEnter={() => setShowProductMenu(true)}
                          onMouseLeave={() => setShowProductMenu(false)}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {item.submenu.map((subItem) => (
                              <button
                                key={subItem.id}
                                onClick={() => {
                                  setCurrentPage('products');
                                  setShowProductMenu(false);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  padding: '0.75rem 1rem',
                                  borderRadius: '12px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: colors.textPrimary,
                                  fontWeight: '500',
                                  fontSize: '0.95rem',
                                  transition: 'all 0.2s ease',
                                  textAlign: 'left'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.background = `rgba(${parseInt(subItem.color.slice(1, 3), 16)}, ${parseInt(subItem.color.slice(3, 5), 16)}, ${parseInt(subItem.color.slice(5, 7), 16)}, 0.08)`;
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                <div style={{ fontSize: '1.2rem' }}>
                                  {subItem.label.split(' ')[0]}
                                </div>
                                <span>{subItem.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentPage(item.id)}
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      padding: '0.55rem 1.15rem',
                      borderRadius: '999px',
                      transition:
                        'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      color: active
                        ? colors.textPrimary
                        : hovered
                        ? colors.textPrimary
                        : colors.textMuted,
                      background: active
                        ? 'rgba(255, 138, 0, 0.12)'
                        : hovered
                        ? 'rgba(255, 152, 0, 0.08)'
                        : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      letterSpacing: '0.3px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                    }}
                  >
                    {item.label}
                    {active && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-2px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '60%',
                          height: '3px',
                          background:
                            'linear-gradient(90deg, #FF8A00 0%, #FF6A00 100%)',
                          borderRadius: '999px',
                          boxShadow:
                            '0 2px 10px rgba(255, 138, 0, 0.5)',
                          animation:
                            'slideIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}
                      />
                    )}
                  </button>
                );
              })}

              {/* Contact Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginLeft: '1rem',
                paddingLeft: '1rem',
                borderLeft: `1px solid ${colors.borderSoft}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.85rem'
                }}>
                  <MapPin size={14} />
                  <span>Ghaziabad</span>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={() => setCurrentPage('contact')}
                style={{
                  background:
                    'linear-gradient(135deg, #FF8A00 0%, #FF6A00 100%)',
                  color: '#FFFFFF',
                  padding: '0.7rem 1.8rem',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  transition:
                    'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: '0 10px 26px rgba(255, 138, 0, 0.35)',
                  marginLeft: '0.9rem',
                  letterSpacing: '0.3px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow =
                    '0 14px 36px rgba(255, 138, 0, 0.45)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 10px 26px rgba(255, 138, 0, 0.35)';
                }}
              >
                <Phone size={18} />
                Get in Touch
              </button>
            </div>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button
              style={{
                background: mobileMenuOpen
                  ? 'rgba(255, 138, 0, 0.12)'
                  : 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.45rem 0.6rem',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition:
                  'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X
                  style={{
                    color: colors.textPrimary,
                    width: '26px',
                    height: '26px',
                  }}
                />
              ) : (
                <Menu
                  style={{
                    color: colors.textMuted,
                    width: '26px',
                    height: '26px',
                  }}
                />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isMobile && (
        <div
          style={{
            maxHeight: mobileMenuOpen ? '600px' : '0',
            overflow: 'hidden',
            transition:
              'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            background: colors.softBg,
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderTop: mobileMenuOpen
              ? `1px solid ${colors.borderSoft}`
              : 'none',
          }}
        >
          <div style={{ padding: '0.85rem 1rem 1.2rem' }}>
            {navItems.map((item) => {
              const active = currentPage === item.id;
              
              if (item.id === 'products') {
                return (
                  <div key={item.id} style={{ marginBottom: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setCurrentPage('products');
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.7rem',
                        width: '100%',
                        textAlign: 'left',
                        padding: '1.05rem 1.4rem',
                        margin: '0.3rem 0',
                        color: active
                          ? colors.textPrimary
                          : colors.textMuted,
                        background: active
                          ? 'rgba(255, 138, 0, 0.12)'
                          : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '14px',
                        fontWeight: active ? 700 : 600,
                        fontSize: '0.98rem',
                        transition:
                          'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        letterSpacing: '0.3px',
                        position: 'relative',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = active
                          ? 'rgba(255, 138, 0, 0.14)'
                          : 'rgba(255, 138, 0, 0.08)';
                        e.currentTarget.style.paddingLeft = '1.7rem';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = active
                          ? 'rgba(255, 138, 0, 0.12)'
                          : 'transparent';
                        e.currentTarget.style.paddingLeft = '1.4rem';
                      }}
                    >
                      <span
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '999px',
                          background: active
                            ? 'linear-gradient(135deg, #FF8A00, #FF6A00)'
                            : 'rgba(0,0,0,0.12)',
                        }}
                      />
                      <span>{item.label}</span>
                      {active && (
                        <div
                          style={{
                            marginLeft: 'auto',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background:
                              'linear-gradient(135deg, #FF8A00, #FF6A00)',
                            boxShadow:
                              '0 0 12px rgba(255, 138, 0, 0.7)',
                          }}
                        />
                      )}
                    </button>
                    
                    {/* Mobile Product Submenu */}
                    <div style={{ 
                      paddingLeft: '2rem',
                      marginTop: '0.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}>
                      {item.submenu.map((subItem) => (
                        <button
                          key={subItem.id}
                          onClick={() => {
                            setCurrentPage('products');
                            setMobileMenuOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            background: 'rgba(255, 138, 0, 0.05)',
                            border: 'none',
                            borderRadius: '10px',
                            color: colors.textMuted,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = `rgba(${parseInt(subItem.color.slice(1, 3), 16)}, ${parseInt(subItem.color.slice(3, 5), 16)}, ${parseInt(subItem.color.slice(5, 7), 16)}, 0.1)`;
                            e.currentTarget.style.color = colors.textPrimary;
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 138, 0, 0.05)';
                            e.currentTarget.style.color = colors.textMuted;
                          }}
                        >
                          <div style={{ fontSize: '1.1rem' }}>
                            {subItem.label.split(' ')[0]}
                          </div>
                          <span>{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentPage(item.id);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.7rem',
                    width: '100%',
                    textAlign: 'left',
                    padding: '1.05rem 1.4rem',
                    margin: '0.3rem 0',
                    color: active
                      ? colors.textPrimary
                      : colors.textMuted,
                    background: active
                      ? 'rgba(255, 138, 0, 0.12)'
                      : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '14px',
                    fontWeight: active ? 700 : 600,
                    fontSize: '0.98rem',
                    transition:
                      'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    letterSpacing: '0.3px',
                    position: 'relative',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = active
                      ? 'rgba(255, 138, 0, 0.14)'
                      : 'rgba(255, 138, 0, 0.08)';
                    e.currentTarget.style.paddingLeft = '1.7rem';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = active
                      ? 'rgba(255, 138, 0, 0.12)'
                      : 'transparent';
                    e.currentTarget.style.paddingLeft = '1.4rem';
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '999px',
                      background: active
                        ? 'linear-gradient(135deg, #FF8A00, #FF6A00)'
                        : 'rgba(0,0,0,0.12)',
                    }}
                  />
                  <span>{item.label}</span>
                  {active && (
                    <div
                      style={{
                        marginLeft: 'auto',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, #FF8A00, #FF6A00)',
                        boxShadow:
                          '0 0 12px rgba(255, 138, 0, 0.7)',
                      }}
                    />
                  )}
                </button>
              );
            })}

            {/* Mobile Contact Info */}
            <div style={{
              padding: '1.05rem 1.4rem',
              margin: '0.3rem 0',
              color: colors.textMuted,
              fontSize: '0.9rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              borderTop: `1px solid ${colors.borderSoft}`,
              marginTop: '0.5rem',
              paddingTop: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <MapPin size={16} />
                <span>Ghaziabad, Uttar Pradesh</span>
              </div>
            </div>

            <button
              onClick={() => {
                setCurrentPage('contact');
                setMobileMenuOpen(false);
              }}
              style={{
                width: '100%',
                background:
                  'linear-gradient(135deg, #FF8A00 0%, #FF6A00 100%)',
                color: 'white',
                padding: '1.05rem 1.4rem',
                borderRadius: '14px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '1.02rem',
                marginTop: '0.9rem',
                boxShadow: '0 10px 26px rgba(255, 138, 0, 0.35)',
                transition: 'all 0.28s ease',
                letterSpacing: '0.3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 14px 36px rgba(255, 138, 0, 0.45)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 10px 26px rgba(255, 138, 0, 0.35)';
              }}
            >
              <Phone size={18} />
              Get in Touch
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            width: 0;
            opacity: 0;
          }
          to {
            width: 60%;
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -10px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </nav>
  );
}

export default Navbar;